// Radiology RIS lite (docs/plans/phase2-radiology.md §3.3). Internal IDs only in paths.
import { queryOptions } from '@tanstack/react-query';
import {
  imagingExamSchema,
  imagingProcedureSchema,
  imagingProceduresSchema,
  imagingReportSchema,
  imagingWorklistSchema,
  patientImagingReportsSchema,
  type AmendImagingReportRequest,
  type ImagingWorklistState,
  type PerformImagingRequest,
  type ScheduleImagingRequest,
  type SignImagingReportRequest,
  type UpdateImagingProcedureRequest,
} from '@wodi/shared';

import { api } from './client.ts';

/** The worklist refreshes every 30 seconds, like the lab's and the bed board. */
export const imagingWorklistQuery = (state: ImagingWorklistState) =>
  queryOptions({
    queryKey: ['imaging', 'worklist', state],
    queryFn: () =>
      api('GET', `/imaging/worklist?state=${state}`, { schema: imagingWorklistSchema }),
    refetchInterval: 30_000,
  });

export const imagingExamQuery = (orderId: string) =>
  queryOptions({
    queryKey: ['imaging', 'exam', orderId],
    queryFn: () => api('GET', `/imaging/exams/${orderId}`, { schema: imagingExamSchema }),
  });

export const scheduleImagingExam = (orderId: string, body: ScheduleImagingRequest) =>
  api('POST', `/imaging/exams/${orderId}/schedule`, { body, schema: imagingExamSchema });

export const performImagingExam = (orderId: string, body: PerformImagingRequest) =>
  api('POST', `/imaging/exams/${orderId}/perform`, { body, schema: imagingExamSchema });

export const setImagesLink = (orderId: string, imagesLink: string | null) =>
  api('POST', `/imaging/exams/${orderId}/images-link`, {
    body: { imagesLink },
    schema: imagingExamSchema,
  });

export const signImagingReport = (orderId: string, body: SignImagingReportRequest) =>
  api('POST', `/imaging/exams/${orderId}/report`, { body, schema: imagingExamSchema });

export const amendImagingReport = (reportId: string, body: AmendImagingReportRequest) =>
  api('POST', `/imaging-reports/${reportId}/amend`, { body, schema: imagingExamSchema });

export const verifyImagingReport = (reportId: string, version: number) =>
  api('POST', `/imaging-reports/${reportId}/verify`, {
    body: { version },
    schema: imagingExamSchema,
  });

export const imagingReportQuery = (reportId: string) =>
  queryOptions({
    queryKey: ['imaging', 'report', reportId],
    queryFn: () => api('GET', `/imaging-reports/${reportId}`, { schema: imagingReportSchema }),
  });

export const recordImagingReportPrinted = (reportId: string, copy: number, version: number) =>
  api('POST', `/imaging-reports/${reportId}/printed`, { body: { copy, version } });

export const patientImagingReportsQuery = (patientId: string) =>
  queryOptions({
    queryKey: ['imaging', 'patient', patientId],
    queryFn: () =>
      api('GET', `/patients/${patientId}/imaging-reports`, {
        schema: patientImagingReportsSchema,
      }),
  });

// --- Settings > Imaging --------------------------------------------------------------------

export const imagingProceduresQuery = queryOptions({
  queryKey: ['imaging', 'procedures'],
  queryFn: () => api('GET', '/admin/imaging-procedures', { schema: imagingProceduresSchema }),
});

export const updateImagingProcedure = (
  serviceItemId: string,
  body: UpdateImagingProcedureRequest,
) =>
  api('PUT', `/admin/imaging-procedures/${serviceItemId}`, {
    body,
    schema: imagingProcedureSchema,
  });
